import { ReactElement, useState } from "react";
import { PageContainer, PageTitle, InfoRow, SkeletonLoader, StatCard } from "../../global/Common";
import { Box, Card, CardContent, Typography, Skeleton, Divider, useMediaQuery, useTheme, Button, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useUserData } from "../../context/UserDataContext";
import { useLanguage } from "../../context/LanguageContext";
import { useSettings } from "../../context/SettingsContext";
import EditAccountDialog from "./EditAccountDialog";
import { getWayTypeName } from "../../constants/wayTypes";
import { useBrand } from "../../context/BrandContext";
import { toDisplayName } from "../../utils/text";

const MonCompte = (): ReactElement => {
  const { t } = useLanguage();
  const { personData, loading, refetch } = useUserData();
  const { isDiamondsEnabled } = useSettings();
  const { theme: brandTheme } = useBrand();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const person = personData?.PersonResponse;
  const contacts = person?.ContactsResponse;
  const address = contacts?.AddressResponse;
  const accounts = personData?.AccountsResponse?.AccountResponse || [];
  const totalPoints = accounts.reduce((total, account) => total + (account.value || 0), 0);
  
  const totalDiamonds = Math.floor(Math.random() * 100) + 1;
  
  const entityIdentifier = person?.EntityResponse?.entityIdentifier || "";

  const handleEditSuccess = () => {
    refetch();
  };

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {t('pages.account.title')}
        </Typography>
        <Button
          variant="text"
          startIcon={<EditIcon />}
          onClick={() => setIsEditDialogOpen(true)}
          disabled={loading || !personData}
          sx={{
            color: brandTheme.primary,
            '&:hover': {
              backgroundColor: brandTheme.primaryBg,
            },
          }}
        >
          {t('common.edit')}
        </Button>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <i className="bx bx-user" style={{ fontSize: 24, color: brandTheme.primary }} />
                {t('pages.account.personalInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {loading ? <SkeletonLoader /> : person ? (
                <Box>
                  <InfoRow label={t('pages.account.lastName')} value={person.PersonResponse?.lastName ? toDisplayName(person.PersonResponse.lastName) : undefined} />
                  <InfoRow label={t('pages.account.firstName')} value={person.PersonResponse?.firstName ? toDisplayName(person.PersonResponse.firstName) : undefined} />
                  <InfoRow label={t('pages.account.birthDate')} value={person.PersonResponse?.birthDate} />
                  <InfoRow label={t('pages.account.email')} value={contacts?.EmailResponse?.emailValue} />
                  <InfoRow label={t('pages.account.mobile')} value={contacts?.CellPhoneResponse?.number} />
                </Box>
              ) : (
                <Typography color="text.secondary">{t('common.noData')}</Typography>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <i className="bx bx-map" style={{ fontSize: 24, color: brandTheme.primary }} />
                {t('pages.account.address')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {loading ? <SkeletonLoader /> : address ? (
                <Box>
                  {(address.address3Num || address.address3Name || address.address1 || address.address2) && (
                    <Typography variant="body1" sx={{ color: 'text.primary', mb: 0.5 }}>
                      {[
                        address.address3Num && address.address3Name && `${address.address3Num} ${address.address3Type ? getWayTypeName(address.address3Type) : ''} ${address.address3Name}`.trim(),
                        address.address1 && `${t('pages.account.building')} ${address.address1}`,
                        address.address2 && `${t('pages.account.apartment')} ${address.address2}`
                      ].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  
                  {(address.address4 || address.city || address.zipCode) && (
                    <Typography variant="body1" sx={{ color: 'text.primary', mb: 0.5 }}>
                      {[
                        address.address4,
                        address.city && address.zipCode && `${address.city} ${address.zipCode}`,
                        !address.zipCode && address.city,
                        !address.city && address.zipCode
                      ].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  
                  {address.country && (
                    <Typography variant="body1" sx={{ color: 'text.primary', textTransform: 'capitalize' }}>
                      {address.country}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">{t('pages.account.noAddress')}</Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: isDiamondsEnabled ? '1fr 1fr 1fr' : '1fr 1fr' }, gap: 3 }}>
          <StatCard 
            icon="bx bx-star" 
            value={totalPoints} 
            label={t('pages.dashboard.loyaltyPoints')}
            loading={loading}
          />

          {isDiamondsEnabled && (
            <StatCard 
              icon="bx bx-diamond" 
              value={totalDiamonds} 
              label={t('pages.dashboard.diamonds')}
              loading={loading}
              showProgressBar={true}
              maxValue={100}
            />
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('pages.dashboard.myBadges')}</Typography>
              <Divider sx={{ mb: 2 }} />
              {loading ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Skeleton variant="rectangular" width={80} height={32} />
                  <Skeleton variant="rectangular" width={80} height={32} />
                  <Skeleton variant="rectangular" width={80} height={32} />
                </Box>
              ) : (
                <Typography color="text.secondary">{t('pages.dashboard.badgesNotAvailable')}</Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <EditAccountDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        personData={personData || null}
        entityIdentifier={entityIdentifier}
        onSuccess={handleEditSuccess}
      />
    </PageContainer>
  );
};

export default MonCompte;
